// The page side of the ember scene: probe, poster or live, the lazily loaded
// engine, the frame loop and its governor, the pointer, and every lifecycle
// event a browser can throw at a canvas. Nothing here draws.
//
// Lifecycle: a hidden tab, a frozen page or (for the hearth) a scene scrolled
// off screen stops the loop. A lost GPU context shows the poster at once (the
// loss event arrives after the fact, so every frame also asks
// gl.isContextLost()); a restore rebuilds behind the poster. Pages nobody has
// touched for a while hold 30 fps by the clock; the low tier always does.

import { IDLE_AFTER_MS, IDLE_FPS, TIERS } from './config'
import type { TierName, Variant } from './config'
import type { Engine } from './engine'
import { Governor } from './governor'
import type { GovernorAction } from './governor'
import { readPalette } from './palette'
import { decide, readDevice } from './probe'

export type SceneMode = 'pending' | 'live' | 'poster'

export interface EmberOptions {
  variant: Variant
  /** The square box the page draws the mark in; the embers land on it. */
  anchor: () => HTMLElement | null
  /** The poster's land, measured so the live ridges line up with it (gate). */
  land?: () => Element | null
  /** Where pointer input is read (the hearth's container); the gate listens to the window. */
  surface?: () => HTMLElement | null
  onMode: (mode: SceneMode, reason: string) => void
}

export interface EmberHook {
  ready: Promise<SceneMode>
  mode: () => SceneMode
  reason: () => string
  tier: () => TierName | null
  /** Jump the intro clock (99 skips it). */
  setIntro: (seconds: number) => void
  /** Freeze the animation clock (null releases it). */
  setClock: (seconds: number | null) => void
  /** Move the pointer in CSS px (canvas-relative); `down` sends a pulse. */
  pointer: (x: number, y: number, down?: boolean) => void
  /** Stop the loop and draw one frame now (stills on slow renderers); `resume` restarts it. */
  still: () => void
  resume: () => void
  stats: () => { fps: number; tier: TierName | null; scale: number; particles: number; width: number; height: number }
}

declare global {
  interface Window {
    __EMBER__?: Partial<Record<Variant, EmberHook>>
  }
}

const PACE_FRAMES = 5
const HEARTH_AWAKE_MS = 2500

/** Starts the scene on `canvas`; returns the function that stops it and frees the GPU. */
export function startEmber(canvas: HTMLCanvasElement, options: EmberOptions): () => void {
  const { variant } = options
  let disposed = false
  let mode: SceneMode = 'pending'
  let reason = 'starting'
  let tierName: TierName | null = null
  let gl: WebGL2RenderingContext | null = null
  let engine: Engine | null = null
  let governor: Governor | null = null
  let raf = 0
  let running = false
  let visible = true
  let onScreen = true
  let lastDrawn = 0
  let animTime = 0
  let frozenClock: number | null = null
  let introStart = 0
  let introOffset = 0
  let fpsEstimate = 0
  let lossCount = 0
  let forced = false
  let pacing: { count: number; last: number; min: number } | null = null
  let readyResolve: (value: SceneMode) => void = () => {}
  const ready = new Promise<SceneMode>(resolve => { readyResolve = resolve })

  const pointer = { x: -1e4, y: -1e4, tx: -1e4, ty: -1e4, strength: 0, target: 0, vx: 0, vy: 0, lastMove: performance.now() }
  let pulse: { x: number; y: number; t0: number } | null = null

  function report(next: SceneMode, why: string) {
    reason = why
    if (next === mode) return
    mode = next
    options.onMode(next, why)
    if (next !== 'pending') readyResolve(next)
  }

  function canvasRect() {
    return canvas.getBoundingClientRect()
  }

  function landHeight(): number {
    const land = options.land?.()?.getBoundingClientRect()
    if (land && land.height > 0) return land.height
    const spacing = parseFloat(getComputedStyle(document.documentElement).fontSize || '16') * 0.25
    return Math.min(window.innerHeight * 0.34, spacing * 88)
  }

  function layout() {
    if (!engine) return
    const rect = canvasRect()
    engine.resize(rect.width, rect.height, window.devicePixelRatio || 1, governor?.scale ?? 1, landHeight())
  }

  function schedule() {
    if (!raf && running && visible && onScreen && !disposed) raf = requestAnimationFrame(frame)
  }

  function start() {
    running = true
    lastDrawn = 0
    schedule()
  }

  function stop() {
    running = false
    if (raf) cancelAnimationFrame(raf)
    raf = 0
  }

  function teardownEngine() {
    stop()
    engine?.dispose()
    engine = null
  }

  function toPoster(why: string) {
    teardownEngine()
    if (gl && !gl.isContextLost()) gl.getExtension('WEBGL_lose_context')?.loseContext()
    report('poster', why)
  }

  function capMs(now: number): number {
    const tier = tierName ? TIERS[tierName] : null
    let cap = tier?.fpsCap ? 1000 / tier.fpsCap : 0
    const idleFor = now - pointer.lastMove
    if (variant === 'hearth' ? idleFor > HEARTH_AWAKE_MS : idleFor > IDLE_AFTER_MS) cap = Math.max(cap, 1000 / IDLE_FPS)
    return cap
  }

  function apply(action: GovernorAction | null) {
    if (!action || !engine || !governor) return
    if (action.kind === 'scale') layout()
    else if (action.kind === 'tier') {
      tierName = action.tier
      engine.setTier(TIERS[action.tier])
      governor.reset(TIERS[action.tier])
      layout()
    } else if (action.kind === 'pace') pacing = { count: 0, last: 0, min: Number.POSITIVE_INFINITY }
    else toPoster('governor')
  }

  function frame(now: number, once = false) {
    raf = 0
    if (disposed || !engine || !gl || (!running && !once)) return
    if (gl.isContextLost()) {
      onLost()
      return
    }
    if (pacing) {
      // Draw nothing: the callbacks now arrive at the browser's own pace.
      if (pacing.last) pacing.min = Math.min(pacing.min, now - pacing.last)
      pacing.last = now
      if (++pacing.count > PACE_FRAMES) {
        governor?.pace(pacing.min, now)
        pacing = null
        lastDrawn = 0
      }
      schedule()
      return
    }
    const cap = capMs(now)
    if (!once && cap && lastDrawn && now - lastDrawn < cap - 1.5) {
      schedule()
      return
    }
    const dt = lastDrawn ? now - lastDrawn : 0
    lastDrawn = now
    if (dt > 0 && dt < 1000) {
      fpsEstimate = fpsEstimate ? fpsEstimate * 0.92 + (1000 / dt) * 0.08 : 1000 / dt
      // A forced tier (?ember=) holds still for tests and support.
      if (!forced) apply(governor?.sample(dt, now, cap) ?? null)
      if (!engine) return
    }
    if (frozenClock === null) animTime += Math.min(dt, 100) / 1000

    const rect = canvasRect()
    const anchor = options.anchor()?.getBoundingClientRect()
    const box = engine.markBox
    const size = anchor && anchor.width > 0 ? anchor.width : Math.min(rect.width, rect.height) * 0.5
    const left = anchor ? anchor.left - rect.left : (rect.width - size) / 2
    const top = anchor ? anchor.top - rect.top : (rect.height - size) / 2

    // Ease the pointer: position follows quickly, strength fades in and out.
    const ease = Math.min(1, dt / 90)
    const px = pointer.x
    const py = pointer.y
    pointer.x += (pointer.tx - pointer.x) * (pointer.x < -9e3 ? 1 : ease * 2.2)
    pointer.y += (pointer.ty - pointer.y) * (pointer.y < -9e3 ? 1 : ease * 2.2)
    if (dt > 0) {
      pointer.vx += (((pointer.x - px) / dt) * 1000 - pointer.vx) * ease
      pointer.vy += (((pointer.y - py) / dt) * 1000 - pointer.vy) * ease
    }
    const resting = now - pointer.lastMove > 1400
    pointer.strength += ((resting ? 0 : pointer.target) - pointer.strength) * Math.min(1, dt / 260)

    const pulseAge = pulse ? (now - pulse.t0) / 1000 : -1
    if (pulse && pulseAge > 2.5) pulse = null
    const intro = (now - introStart) / 1000 + introOffset
    engine.render({
      time: frozenClock ?? animTime,
      intro,
      mark: { x: left + box.centre[0] * size, y: top + box.centre[1] * size, r: box.radius * size },
      pointer: { x: pointer.x, y: pointer.y, strength: pointer.strength },
      pointerVel: { x: pointer.vx, y: pointer.vy },
      pulse: pulse ? { x: pulse.x, y: pulse.y, age: pulseAge } : null,
    })
    if (mode !== 'live') report('live', tierName ?? 'live')
    if (!once) schedule()
  }

  async function build(restoring: boolean) {
    const loss = lossCount
    if (!gl || !tierName) return
    try {
      const { Engine } = await import('./engine')
      if (disposed || loss !== lossCount || gl.isContextLost()) return
      const built = await Engine.create(gl, variant, TIERS[tierName], readPalette())
      if (disposed || loss !== lossCount || gl.isContextLost()) {
        built.dispose()
        return
      }
      engine = built
      governor ??= new Governor(TIERS[tierName])
      layout()
      if (!restoring) introStart = performance.now()
      start()
    } catch (error) {
      if (disposed || loss !== lossCount) return
      console.warn('[ember] staying on the poster:', error instanceof Error ? error.message : error)
      toPoster('build-failed')
    }
  }

  let restoreTimer = 0
  function onLost(event?: Event) {
    event?.preventDefault()
    if (!engine && mode === 'poster') return
    lossCount++
    stop()
    engine = null
    report('poster', 'context-lost')
    window.clearTimeout(restoreTimer)
    // A restore that never comes leaves the poster up for good.
    restoreTimer = window.setTimeout(() => { restoreTimer = 0 }, 10000)
  }
  function onRestored() {
    if (disposed || !restoreTimer) return
    window.clearTimeout(restoreTimer)
    restoreTimer = 0
    void build(true)
  }

  // ---------- events ----------

  const surface = () => (variant === 'gate' ? null : options.surface?.() ?? canvas.parentElement)
  const target: EventTarget = surface() ?? window
  function toLocal(event: PointerEvent) {
    const rect = canvasRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }
  function onMove(event: PointerEvent) {
    const point = toLocal(event)
    pointer.tx = point.x
    pointer.ty = point.y
    pointer.target = 1
    const wasResting = performance.now() - pointer.lastMove > IDLE_AFTER_MS
    pointer.lastMove = performance.now()
    if (wasResting) lastDrawn = 0
    schedule()
  }
  function onDown(event: PointerEvent) {
    onMove(event)
    const point = toLocal(event)
    pulse = { x: point.x, y: point.y, t0: performance.now() }
  }
  function onLeave() {
    pointer.target = 0
  }
  // On phones the mark scrolls with the page; touch scrolling sends no pointer moves.
  function onScroll() {
    pointer.lastMove = performance.now()
    schedule()
  }
  function onVisibility() {
    visible = document.visibilityState === 'visible'
    if (visible) {
      lastDrawn = 0
      schedule()
    } else if (raf) {
      cancelAnimationFrame(raf)
      raf = 0
    }
  }
  function onFreeze() {
    visible = false
    if (raf) cancelAnimationFrame(raf)
    raf = 0
  }

  const reduced = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
  function onReducedMotion() {
    if (reduced?.matches && !new URLSearchParams(location.search).get('ember')) toPoster('reduced-motion')
  }

  let dprQuery: MediaQueryList | null = null
  function watchDpr() {
    dprQuery?.removeEventListener('change', onDpr)
    if (typeof window.matchMedia !== 'function') return
    dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
    dprQuery.addEventListener('change', onDpr)
  }
  function onDpr() {
    layout()
    watchDpr()
  }

  const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => layout()) : null
  const intersect = variant === 'hearth' && typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(entries => {
      onScreen = entries.some(entry => entry.isIntersecting)
      if (onScreen) {
        lastDrawn = 0
        schedule()
      }
    })
    : null
  const theme = typeof MutationObserver !== 'undefined'
    ? new MutationObserver(() => engine?.setPalette(readPalette()))
    : null

  function listen() {
    target.addEventListener('pointermove', onMove as EventListener, { passive: true })
    target.addEventListener('pointerdown', onDown as EventListener, { passive: true })
    ;(variant === 'gate' ? document.documentElement : (target as HTMLElement)).addEventListener('pointerleave', onLeave)
    window.addEventListener('blur', onLeave)
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })
    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('freeze', onFreeze)
    document.addEventListener('resume', onVisibility)
    canvas.addEventListener('webglcontextlost', onLost)
    canvas.addEventListener('webglcontextrestored', onRestored)
    reduced?.addEventListener('change', onReducedMotion)
    resize?.observe(canvas)
    intersect?.observe(canvas)
    theme?.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    watchDpr()
  }

  function unlisten() {
    target.removeEventListener('pointermove', onMove as EventListener)
    target.removeEventListener('pointerdown', onDown as EventListener)
    ;(variant === 'gate' ? document.documentElement : (target as HTMLElement)).removeEventListener('pointerleave', onLeave)
    window.removeEventListener('blur', onLeave)
    window.removeEventListener('scroll', onScroll, { capture: true })
    document.removeEventListener('visibilitychange', onVisibility)
    document.removeEventListener('freeze', onFreeze)
    document.removeEventListener('resume', onVisibility)
    canvas.removeEventListener('webglcontextlost', onLost)
    canvas.removeEventListener('webglcontextrestored', onRestored)
    reduced?.removeEventListener('change', onReducedMotion)
    dprQuery?.removeEventListener('change', onDpr)
    resize?.disconnect()
    intersect?.disconnect()
    theme?.disconnect()
  }

  // ---------- test hook ----------

  const hook: EmberHook = {
    ready,
    mode: () => mode,
    reason: () => reason,
    tier: () => tierName,
    setIntro: seconds => {
      introStart = performance.now()
      introOffset = seconds
      schedule()
    },
    setClock: seconds => {
      frozenClock = seconds
      schedule()
    },
    pointer: (x, y, down = false) => {
      pointer.tx = x
      pointer.ty = y
      pointer.target = 1
      pointer.lastMove = performance.now()
      if (down) pulse = { x, y, t0: performance.now() }
      schedule()
    },
    still: () => {
      stop()
      frame(performance.now(), true)
    },
    resume: () => start(),
    stats: () => ({
      fps: Math.round(fpsEstimate),
      tier: tierName,
      scale: governor?.scale ?? 1,
      particles: engine?.particleCount ?? 0,
      width: engine?.size.width ?? 0,
      height: engine?.size.height ?? 0,
    }),
  }
  window.__EMBER__ = { ...window.__EMBER__, [variant]: hook }

  // ---------- boot ----------

  const decision = decide(readDevice())
  if (decision.mode === 'poster') {
    report('poster', decision.reason)
  } else {
    tierName = decision.tier
    forced = decision.reason === 'forced'
    gl = canvas.getContext('webgl2', {
      alpha: variant === 'hearth',
      premultipliedAlpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
      powerPreference: decision.tier === 'high' && variant === 'gate' ? 'high-performance' : 'default',
    })
    if (!gl) {
      report('poster', 'no-context')
    } else {
      listen()
      void build(false)
    }
  }

  return () => {
    disposed = true
    unlisten()
    window.clearTimeout(restoreTimer)
    teardownEngine()
    if (gl && !gl.isContextLost()) gl.getExtension('WEBGL_lose_context')?.loseContext()
    gl = null
    if (window.__EMBER__?.[variant] === hook) {
      const rest = { ...window.__EMBER__ }
      delete rest[variant]
      window.__EMBER__ = rest
    }
    readyResolve(mode)
  }
}
